import React, { useState } from 'react';
import { ArrowDownUp, Loader2 } from 'lucide-react';

const TOKENS = ['ETH', 'USDC', 'BETH'];
const sel = 'px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm';
const LABELS = { base: { BETH: 'BETH USD (Official)' }, mainnet: { BETH: 'Bethcoin (old)' } };
const tokLabel = (m, t) => LABELS[m]?.[t] || t;
const money = (v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const LowValueWarning = ({ quote, checked, onChange, prefix }) => (
  <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-200 space-y-2" data-testid={`${prefix}-low-value-warning`}>
    <p className="font-semibold">You'd get back only {money(quote.usd_out)} for {money(quote.usd_in)} of value ({Math.max(quote.value_ratio * 100, 0.01).toFixed(2)}%).</p>
    <p>The pool doesn't have enough on the other side to pay a fair price for this amount.</p>
    <label className="flex items-center gap-2 text-red-100"><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={`${prefix}-low-value-confirm`} /> I understand and still want this swap</label>
  </div>
);

export const SwapForm = ({ onQuote, onSubmit, submitLabel, busy, quote, prefix, wallets, onChange }) => {
  const [f, setF] = useState({ market: 'base', token_in: 'ETH', token_out: 'USDC', amount: '', slippage_pct: 1, wallet: 'treasury' });
  const [ok, setOk] = useState(false);
  const update = (n) => { setF(n); setOk(false); onChange?.(n); };
  const set = (k, v) => update({ ...f, [k]: v });
  const body = { ...f, amount: parseFloat(f.amount), confirm_low_value: ok };
  if (!wallets) delete body.wallet;
  const valid = body.amount > 0 && f.token_in !== f.token_out;
  const blocked = quote?.low_value && !ok;
  return (
    <div className="space-y-3" data-testid={`${prefix}-swap-form`}>
      {wallets && <select value={f.wallet} onChange={(e) => set('wallet', e.target.value)} className={`${sel} w-full`} data-testid={`${prefix}-wallet`}>
        {wallets.map(w => <option key={w.id} value={w.id}>Pay from: {w.label}</option>)}
      </select>}
      <select value={f.market} onChange={(e) => set('market', e.target.value)} className={`${sel} w-full`} data-testid={`${prefix}-market`}>
        <option value="base">Uniswap · Base</option>
        <option value="mainnet">Uniswap · Ethereum mainnet</option>
      </select>
      <div className="flex gap-2">
        <input type="number" min="0" step="any" value={f.amount} onChange={(e) => set('amount', e.target.value)} placeholder="Amount"
          className={`${sel} flex-1`} data-testid={`${prefix}-amount`} />
        <select value={f.token_in} onChange={(e) => set('token_in', e.target.value)} className={sel} data-testid={`${prefix}-token-in`}>
          {TOKENS.map(t => <option key={t} value={t}>{tokLabel(f.market, t)}</option>)}
        </select>
      </div>
      <button type="button" onClick={() => update({ ...f, token_in: f.token_out, token_out: f.token_in })} data-testid={`${prefix}-flip`}
        className="mx-auto flex p-2 rounded-full bg-slate-700 hover:bg-slate-600"><ArrowDownUp className="w-4 h-4 text-white" /></button>
      <div className="flex gap-2 items-center">
        <div className={`${sel} flex-1 text-slate-300`} data-testid={`${prefix}-amount-out`}>{quote ? `≈ ${Number(quote.amount_out).toLocaleString(undefined, { maximumFractionDigits: 6 })}` : 'You receive'}</div>
        <select value={f.token_out} onChange={(e) => set('token_out', e.target.value)} className={sel} data-testid={`${prefix}-token-out`}>
          {TOKENS.map(t => <option key={t} value={t}>{tokLabel(f.market, t)}</option>)}
        </select>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-400">Max slippage
        <select value={f.slippage_pct} onChange={(e) => set('slippage_pct', parseFloat(e.target.value))} className={sel} data-testid={`${prefix}-slippage`}>
          {[0.5, 1, 2, 3].map(s => <option key={s} value={s}>{s}%</option>)}
        </select>
      </label>
      {quote && <p className="text-xs text-slate-400" data-testid={`${prefix}-quote-info`}>Rate 1 {quote.token_in} ≈ {Number(quote.price).toLocaleString(undefined, { maximumFractionDigits: 6 })} {quote.token_out} · pool fee {quote.fee_tier / 10000}%{quote.service_fee > 0 && ` · service fee ${quote.service_fee_pct}% (${Number(quote.service_fee).toLocaleString(undefined, { maximumFractionDigits: 8 })} ${quote.token_in})`}</p>}
      {quote?.low_value && <LowValueWarning quote={quote} checked={ok} onChange={setOk} prefix={prefix} />}
      <div className="grid grid-cols-2 gap-2">
        <button type="button" disabled={!valid || busy} onClick={() => onQuote(body)} data-testid={`${prefix}-quote-btn`}
          className="py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 text-white rounded-lg text-sm">Get quote</button>
        <button type="button" disabled={!valid || busy || !quote || blocked} onClick={() => onSubmit(body)} data-testid={`${prefix}-submit-btn`}
          className="py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
        </button>
      </div>
    </div>
  );
};

export const SwapStatus = ({ s }) => {
  const tone = { pending: 'bg-amber-500/20 text-amber-300', executing: 'bg-cyan-500/20 text-cyan-300', completed: 'bg-emerald-500/20 text-emerald-300',
    awaiting_payout: 'bg-violet-500/20 text-violet-300', failed: 'bg-red-500/20 text-red-300', rejected: 'bg-slate-600 text-slate-300' }[s] || 'bg-slate-600 text-slate-300';
  return <span className={`text-xs px-2 py-0.5 rounded capitalize ${tone}`} data-testid="request-status">{String(s).replace('_', ' ')}</span>;
};
